import type { Action } from '../core';
import type { CanMode } from './useCan';

/**
 * How a component-level gate asks its question. Exactly one of the three, so `action` and `all`
 * cannot be given together and quietly disagree.
 *
 * `all` is deliberately available as well as `any`, because AND is what several gates want.
 */
export type Asked =
  | { action: Action; all?: never; any?: never }
  | { action?: never; all: Action[]; any?: never }
  | { action?: never; all?: never; any: Action[] };

/** The same fields without the exactly-one constraint, for reading them back off destructured props. */
type AskedFields = { action?: Action; all?: Action[]; any?: Action[] };

/** Turns what a gate was given into what `useCan` takes. */
export const resolveAsked = ({ action, all, any }: AskedFields): { actions: Action | Action[]; mode: CanMode } => ({
  // The `?? []` is unreachable through `Asked`, which requires one of the three. It denies rather
  // than throwing, so a JavaScript caller cannot open a gate by asking nothing.
  actions: action ?? all ?? any ?? [],
  mode: any ? 'any' : 'all',
});
